import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `shell` in Brazilian Portuguese. Declared `: Messages['shell']`, never inferred, so a key
 * that is missing here, a key that exists here and not in English, or a function whose
 * signature drifted is a `bun run typecheck` failure (PRD-27 LOC-011).
 *
 * Register is `você`, informal-professional (LOC-061). No `o senhor`, and nothing from
 * Portugal: it is *tela*, not *ecrã*, and *pasta*, not *ficheiro*.
 *
 * Two agreement notes a later reader should not have to rediscover:
 *
 *   - The rail's recency headings describe *conversas* (f.), so "Pinned" is **Fixadas** and
 *     "Earlier" is **Mais antigas** — feminine plural adjectives, not the neuter nouns
 *     English gets away with. They can never be shared with another surface's "pinned".
 *   - *execução*, *aprovação* and *solicitação* are all feminine, so anything that waits on
 *     them agrees feminine too.
 *
 * Where English names an administrator in the abstract, this says **quem administra** rather
 * than picking *administrador* or *administradora* for a person nobody has met (D-21). The
 * gendered pair is still the ratified term for a named role; it is simply the wrong tool for
 * a sentence about whoever happens to run the deployment.
 */

/** The same slice the English file takes, so the two signatures are interchangeable. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/** `Intl.PluralRules`, never `count === 1` — Portuguese puts ZERO in the singular category. */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

export const shell: Messages['shell'] = {
  rail: 'Espaço de trabalho',
  openMenu: 'Abrir o menu do espaço de trabalho',
  closeMenu: 'Fechar o menu do espaço de trabalho',
  homeLink: (productName) => `${productName} — ir para a tela inicial`,
  // Term of art, left in English (D-15), and the 9px pill has no room for anything else.
  onPrem: 'on-prem',
  onPremTitle: 'Esta instalação roda dentro da sua própria rede.',

  newConversation: 'Nova conversa',
  newConversationTitle: 'Começar uma nova conversa',

  loadingConversations: 'Carregando suas conversas…',
  noConversations: 'Nada por aqui ainda. Comece uma conversa para vê-la na lista.',
  // "Desafixar" is the form Brazilian products actually use for unpinning; *soltar* and
  // *desprender* read as physical objects rather than as a list that stops moving.
  pinConversation: (title) => `Fixar ${title}`,
  unpinConversation: (title) => `Desafixar ${title}`,
  deleteConversation: (title) => `Excluir ${title}`,

  // Feminine plural throughout: these describe *conversas*.
  conversationGroups: {
    pinned: 'Fixadas',
    today: 'Hoje',
    yesterday: 'Ontem',
    week: 'Esta semana',
    earlier: 'Mais antigas'
  },

  nav: {
    agents: 'Agentes',
    knowledge: 'Conhecimento',
    memory: 'Memória',
    groups: 'Grupos',
    activity: 'Atividade',
    governance: 'Governança',
    people: 'Pessoas'
  },

  // *aprovação* (f.) and *solicitação* (f.), and *aguardando* is invariable — which is what
  // lets the plural form change only the noun. It is also the verb ADR-0018 ratified for
  // "Waiting for review" (*Aguardando revisão*), so the rail and the queue it points at use
  // the same word for the same state.
  approvalsWaiting: (fmt, count) =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} aprovação aguardando`,
      other: `${fmt.formatCount(count)} aprovações aguardando`
    }),
  sharingWaiting: (fmt, count) =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} solicitação de compartilhamento aguardando`,
      other: `${fmt.formatCount(count)} solicitações de compartilhamento aguardando`
    }),

  useLightTheme: 'Usar o tema claro',
  useDarkTheme: 'Usar o tema escuro',
  signOut: 'Sair',

  palette: {
    title: 'Buscar no espaço de trabalho',
    // Shorter than the English, which matters: it also renders inside the header's
    // `sm:min-w-[292px]` button, where Portuguese would otherwise be the string that clips.
    placeholder: 'Buscar conversas, agentes, conhecimento, grupos',
    close: 'Fechar a busca',
    results: 'Resultados',
    looking: 'Buscando…',
    noMatches: 'Nenhum resultado para isso.',
    groups: {
      conversations: 'Conversas',
      agents: 'Agentes',
      knowledge: 'Conhecimento',
      groups: 'Grupos',
      destinations: 'Ir para'
    },
    home: 'Início',
    homeDetail: 'Começar uma conversa',
    yourAgents: 'Seus agentes'
  },

  checkingSession: 'Verificando sua sessão…',

  routeError: {
    title: 'Esta página não carregou',
    body: 'Recarregar costuma resolver, ainda mais logo depois de uma atualização. Se continuar acontecendo, avise quem administra o sistema.',
    reload: 'Recarregar',
    home: 'Ir para a tela inicial',
    details: 'Detalhes para o suporte',
    unknown: 'Erro desconhecido'
  }
}
